import { useEffect } from 'react';
import { ArrowLeft, ReceiptEuro } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CentersControl,
  PanelLoader,
  PublicationPanel,
  StatusSteps,
} from '@/components/billing/BillingPipeline';
import { useBillingRun } from '@/hooks/useBillingRun';
import { moisLabel } from '@/lib/billing-format';
import type { BillingRunStatus } from '@octave/contracts';

const STEPS = ['controle', 'publication'] as const;
type Step = (typeof STEPS)[number];
const isStep = (value: string | undefined): value is Step =>
  value !== undefined && (STEPS as readonly string[]).includes(value);

/** The step a run lands on, from its persisted lifecycle status. */
const canonicalStep = (status: BillingRunStatus): Step =>
  status === 'analyzed' ? 'controle' : 'publication';

export function BillingRunView() {
  const { runId, step } = useParams<{ runId: string; step: string }>();
  const navigate = useNavigate();
  const { data: run, isLoading, isError } = useBillingRun(runId ?? null);

  // Unknown run → back to the index.
  useEffect(() => {
    if (isError) navigate('/o/admin/billing', { replace: true });
  }, [isError, navigate]);

  // No (or invalid) step in the URL → redirect to the run's canonical step.
  useEffect(() => {
    if (!runId || !run || isStep(step)) return;
    navigate(`/o/admin/billing/${runId}/${canonicalStep(run.status)}`, {
      replace: true,
    });
  }, [runId, run, step, navigate]);

  const activeStep = isStep(step) ? step : null;

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/o/admin/billing')}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> Facturation
        </button>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <ReceiptEuro className="h-6 w-6 text-slate-400" />
            {run ? moisLabel(run.periodMonth, run.periodYear) : 'Facturation'}
          </h1>
          {run && <StatusSteps status={run.status} />}
        </div>
      </div>

      {!runId || isLoading || !run || !activeStep ? (
        <PanelLoader />
      ) : activeStep === 'controle' ? (
        <CentersControl runId={runId} />
      ) : (
        <PublicationPanel runId={runId} />
      )}
    </div>
  );
}
